import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  CircularProgress,
  Paper,
  IconButton,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import PeopleIcon from '@mui/icons-material/People';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PaymentIcon from '@mui/icons-material/Payment';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import StorefrontIcon from '@mui/icons-material/Storefront';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import BarChartIcon from '@mui/icons-material/BarChart';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useQuery } from '@apollo/client/react';
import {
  GetDashboardOrganizationsDocument,
  GetDashboardSubscriptionsDocument,
  GetDashboardAccountingDocumentsDocument,
  GetDashboardPaymentsDocument,
  GetDashboardLicencesDocument,
  type AppType,
  type GetDashboardOrganizationsQuery,
  type GetDashboardSubscriptionsQuery,
  type GetDashboardAccountingDocumentsQuery,
  type GetDashboardPaymentsQuery,
  type GetDashboardLicencesQuery,
} from '@/generated/root-types';
import {
  SubscriptionState,
  AccountingDocumentState,
  AccountingDocumentType,
  PaymentState,
} from '@/types/billing';
import { CustomerInviteButton } from '@/components/customer-invite/CustomerInviteDialog';

const ONBOARDING_DISMISS_KEY = 'reseller_dashboard_onboarding_dismissed';

interface GettingStartedBannerProps {
  variant: 'full' | 'compact';
  onDismiss?: () => void;
}

function GettingStartedBanner({ variant, onDismiss }: GettingStartedBannerProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isFull = variant === 'full';

  const benefits = [
    t('dashboard.gettingStarted.benefitCustomers'),
    t('dashboard.gettingStarted.benefitSubscriptions'),
    t('dashboard.gettingStarted.benefitInvoices'),
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: isFull ? 3 : 2 },
        mb: 3,
        position: 'relative',
        bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.12 : 0.08),
        border: 1,
        borderColor: alpha(theme.palette.primary.main, 0.25),
        borderRadius: 2,
      }}
    >
      {onDismiss ? (
        <IconButton
          size="small"
          onClick={onDismiss}
          aria-label={t('dashboard.gettingStarted.dismiss')}
          sx={{ position: 'absolute', top: 8, right: 8, color: 'text.secondary' }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      ) : null}

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          gap: { xs: 2, md: isFull ? 3 : 2 },
          pr: onDismiss ? 4 : 0,
        }}
      >
        {isFull ? (
          <Box
            sx={{
              flexShrink: 0,
              width: 72,
              height: 72,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(theme.palette.primary.main, 0.15),
              alignSelf: { xs: 'center', md: 'flex-start' },
            }}
          >
            <PersonAddIcon sx={{ fontSize: 36, color: 'primary.main' }} />
          </Box>
        ) : null}

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: isFull ? 'stretch' : 'center',
            gap: 1.5,
            flexDirection: isFull ? 'column' : { xs: 'column', sm: 'row' },
            textAlign: isFull ? 'left' : { xs: 'center', sm: 'left' },
          }}
        >
          {!isFull ? (
            <PersonAddIcon sx={{ fontSize: 28, color: 'primary.main', flexShrink: 0 }} />
          ) : null}
          <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
            <Typography
              variant={isFull ? 'h6' : 'subtitle1'}
              fontWeight={600}
              gutterBottom={isFull}
            >
              {isFull
                ? t('dashboard.gettingStarted.title')
                : t('dashboard.gettingStarted.compactTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: isFull ? 2 : 0 }}>
              {isFull
                ? t('dashboard.gettingStarted.description')
                : t('dashboard.gettingStarted.compactDescription')}
            </Typography>
            {isFull ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1, sm: 2 } }}>
                {benefits.map((benefit) => (
                  <Box key={benefit} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <CheckCircleIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                    <Typography variant="body2">{benefit}</Typography>
                  </Box>
                ))}
              </Box>
            ) : null}
          </Box>
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: { xs: 'stretch', md: 'flex-end' },
          }}
        >
          <CustomerInviteButton isLarge={isFull} />
        </Box>
      </Box>
    </Paper>
  );
}

interface ChartEmptyStateProps {
  icon: React.ReactNode;
  title: string;
  hint: string;
  showInvite?: boolean;
}

function ChartEmptyState({ icon, title, hint, showInvite = false }: ChartEmptyStateProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        py: 4,
        px: 2,
        minHeight: 200,
      }}
    >
      <Box sx={{ color: 'text.disabled', mb: 1.5, '& svg': { fontSize: 48 } }}>{icon}</Box>
      <Typography variant="body1" fontWeight={600} gutterBottom>
        {title}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: showInvite ? 2 : 0, maxWidth: 320 }}
      >
        {hint}
      </Typography>
      {showInvite ? <CustomerInviteButton variant="outlined" size="small" /> : null}
    </Box>
  );
}

interface DashboardCardProps {
  title: string;
  value: number | string;
  subtitle?: string;
  icon: React.ReactNode;
  onClick?: () => void;
  loading?: boolean;
  color?: string;
}

function DashboardCard({
  title,
  value,
  subtitle,
  icon,
  onClick,
  loading,
  color,
}: DashboardCardProps) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardActionArea onClick={onClick} disabled={!onClick} sx={{ height: '100%' }}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              mb: 1,
            }}
          >
            <Typography color="text.secondary" variant="body2">
              {title}
            </Typography>
            <Box sx={{ color: color || 'primary.main' }}>{icon}</Box>
          </Box>
          {loading ? (
            <CircularProgress size={24} />
          ) : (
            <Typography variant="h4" sx={{ color: color || 'text.primary' }}>
              {value}
            </Typography>
          )}
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

interface LicencesByAppCardProps {
  gastroCount: number;
  retailCount: number;
  loading: boolean;
  onClick: () => void;
}

function LicencesByAppCard({ gastroCount, retailCount, loading, onClick }: LicencesByAppCardProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  return (
    <Card sx={{ height: '100%' }}>
      <CardActionArea onClick={onClick} sx={{ height: '100%' }}>
        <CardContent sx={{ '&:last-child': { pb: 2 } }}>
          <Typography color="text.secondary" variant="body2" sx={{ mb: 1.5 }}>
            {t('dashboard.licencesWidgetTitle')}
          </Typography>
          {loading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: 72,
              }}
            >
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'stretch', minHeight: 72 }}>
              <Box sx={{ flex: 1, pr: 1.5, borderRight: 1, borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                  <RestaurantIcon
                    sx={{ color: theme.palette.primary.main, fontSize: 22 }}
                    aria-hidden
                  />
                  <Typography component="span" variant="caption" color="text.secondary">
                    {t('dashboard.licencesSplitGastro')}
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ color: theme.palette.primary.main }}>
                  {gastroCount}
                </Typography>
              </Box>
              <Box sx={{ flex: 1, pl: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                  <StorefrontIcon
                    sx={{ color: theme.palette.info.main, fontSize: 22 }}
                    aria-hidden
                  />
                  <Typography component="span" variant="caption" color="text.secondary">
                    {t('dashboard.licencesSplitRetail')}
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ color: theme.palette.info.main }}>
                  {retailCount}
                </Typography>
              </Box>
            </Box>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

interface ChartCardProps {
  title: string;
  children: React.ReactNode;
  loading?: boolean;
}

function ChartCard({ title, children, loading }: ChartCardProps) {
  return (
    <Paper
      sx={{
        p: 2,
        height: '100%',
        minHeight: 300,
        color: 'text.primary',
        '& .recharts-cartesian-axis-tick-value': { fill: 'currentColor' },
        '& .recharts-legend-item-text': { fill: 'currentColor' },
        '& .recharts-pie-label-text': { fill: 'currentColor' },
      }}
    >
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
          <CircularProgress />
        </Box>
      ) : (
        children
      )}
    </Paper>
  );
}

export default function Dashboard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const chartColors = useMemo(
    () => ({
      success: theme.palette.success.main,
      warning: theme.palette.warning.main,
      error: theme.palette.error.main,
      info: theme.palette.info.main,
      primary: theme.palette.primary.main,
      secondary: theme.palette.grey[500],
    }),
    [theme],
  );
  const locale = i18n.language === 'de' ? 'de-DE' : 'en-US';
  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }).format(amount);

  const { data: orgsData, loading: orgsLoading } = useQuery<GetDashboardOrganizationsQuery>(
    GetDashboardOrganizationsDocument,
    {
      variables: { first: 50 },
    },
  );

  const { data: subsData, loading: subsLoading } = useQuery<GetDashboardSubscriptionsQuery>(
    GetDashboardSubscriptionsDocument,
    {
      variables: { first: 50 },
    },
  );

  const { data: docsData, loading: docsLoading } = useQuery<GetDashboardAccountingDocumentsQuery>(
    GetDashboardAccountingDocumentsDocument,
    {
      variables: { first: 50 },
    },
  );

  const { data: paymentsData, loading: paymentsLoading } = useQuery<GetDashboardPaymentsQuery>(
    GetDashboardPaymentsDocument,
    {
      variables: { first: 50 },
    },
  );

  const gastroAppTypes: AppType[] = ['HOSPITALITY', 'POS'];
  const { data: licencesGastroData, loading: licencesGastroLoading } =
    useQuery<GetDashboardLicencesQuery>(GetDashboardLicencesDocument, {
      variables: { first: 1, where: { appType: { in: gastroAppTypes } } },
    });
  const { data: licencesRetailData, loading: licencesRetailLoading } =
    useQuery<GetDashboardLicencesQuery>(GetDashboardLicencesDocument, {
      variables: { first: 1, where: { appType: { eq: 'RETAIL' } } },
    });

  const licencesGastroCount = licencesGastroData?.licences?.totalCount ?? 0;
  const licencesRetailCount = licencesRetailData?.licences?.totalCount ?? 0;
  const licencesSplitLoading = licencesGastroLoading || licencesRetailLoading;

  const customersCount = orgsData?.organizations?.totalCount ?? 0;
  const customers = orgsData?.organizations?.nodes ?? [];
  const subscriptions = subsData?.subscriptions?.nodes ?? [];
  const documents = docsData?.accountingDocuments?.nodes ?? [];
  const payments = paymentsData?.payments?.nodes ?? [];

  // Subscription stats
  const activeSubscriptions = subscriptions.filter(
    (s) => s.state === SubscriptionState.ACTIVE,
  ).length;
  const pendingSubscriptions = subscriptions.filter(
    (s) => s.state === SubscriptionState.PENDING,
  ).length;
  const cancelledSubscriptions = subscriptions.filter(
    (s) => s.state === SubscriptionState.CANCELLED,
  ).length;
  const expiredSubscriptions = subscriptions.filter(
    (s) => s.state === SubscriptionState.EXPIRED,
  ).length;

  // Invoice stats
  const invoices = documents.filter((d) => d.type === AccountingDocumentType.INVOICE);
  const paidInvoices = invoices.filter((d) => d.state === AccountingDocumentState.PAID).length;
  const sentInvoices = invoices.filter((d) => d.state === AccountingDocumentState.SENT).length;
  const overdueInvoices = invoices.filter(
    (d) => d.state === AccountingDocumentState.OVERDUE,
  ).length;
  const draftInvoices = invoices.filter((d) => d.state === AccountingDocumentState.DRAFT).length;

  // Payment stats
  const completedPayments = payments.filter((p) => p.state === PaymentState.COMPLETED).length;
  const pendingPayments = payments.filter((p) => p.state === PaymentState.PENDING).length;
  const failedPayments = payments.filter((p) => p.state === PaymentState.FAILED).length;

  // Revenue calculation
  const totalRevenue = invoices
    .filter((d) => d.state === AccountingDocumentState.PAID)
    .reduce((sum, d) => sum + d.amountGross, 0);

  const openRevenue = invoices
    .filter(
      (d) =>
        d.state === AccountingDocumentState.SENT || d.state === AccountingDocumentState.OVERDUE,
    )
    .reduce((sum, d) => sum + d.amountGross, 0);

  // Chart data (labels from i18n)
  const subscriptionChartData = [
    { name: t('dashboard.chartActive'), value: activeSubscriptions, color: chartColors.success },
    { name: t('dashboard.chartPending'), value: pendingSubscriptions, color: chartColors.warning },
    {
      name: t('dashboard.chartCancelled'),
      value: cancelledSubscriptions,
      color: chartColors.error,
    },
    {
      name: t('dashboard.chartExpired'),
      value: expiredSubscriptions,
      color: chartColors.secondary,
    },
  ].filter((d) => d.value > 0);

  const invoiceChartData = [
    { name: t('dashboard.chartPaid'), value: paidInvoices, color: chartColors.success },
    { name: t('dashboard.chartSent'), value: sentInvoices, color: chartColors.info },
    { name: t('dashboard.chartOverdue'), value: overdueInvoices, color: chartColors.error },
    { name: t('dashboard.chartDraft'), value: draftInvoices, color: chartColors.secondary },
  ].filter((d) => d.value > 0);

  const paymentChartData = [
    { name: t('dashboard.chartCompleted'), value: completedPayments, color: chartColors.success },
    { name: t('dashboard.chartPending'), value: pendingPayments, color: chartColors.warning },
    { name: t('dashboard.chartFailed'), value: failedPayments, color: chartColors.error },
  ].filter((d) => d.value > 0);

  // Monthly revenue (mock aggregation from invoices by month)
  const getMonthlyRevenue = () => {
    const months: Record<string, number> = {};
    invoices
      .filter((d) => d.state === AccountingDocumentState.PAID)
      .forEach((d) => {
        const date = new Date(d.createdAt);
        const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        months[key] = (months[key] || 0) + d.amountGross;
      });
    return Object.entries(months)
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-6)
      .map(([month, value]) => ({
        month: new Date(month + '-01').toLocaleDateString(locale, {
          month: 'short',
          year: '2-digit',
        }),
        revenue: value,
      }));
  };

  const monthlyRevenueData = getMonthlyRevenue();

  const isEmptyDashboard = !orgsLoading && customersCount === 0;
  const [onboardingDismissed, setOnboardingDismissed] = useState(
    () => localStorage.getItem(ONBOARDING_DISMISS_KEY) === 'true',
  );

  const handleDismissOnboarding = () => {
    localStorage.setItem(ONBOARDING_DISMISS_KEY, 'true');
    setOnboardingDismissed(true);
  };

  const showOnboarding = !onboardingDismissed;

  const renderChartEmpty = (icon: React.ReactNode, titleKey: string, hintKey: string) =>
    isEmptyDashboard ? (
      <ChartEmptyState icon={icon} title={t(titleKey)} hint={t(hintKey)} showInvite />
    ) : (
      <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
        {t(titleKey)}
      </Typography>
    );

  return (
    <Box>
      {showOnboarding ? (
        <GettingStartedBanner
          variant={isEmptyDashboard ? 'full' : 'compact'}
          onDismiss={handleDismissOnboarding}
        />
      ) : null}

      {/* KPI Cards */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box sx={{ flex: '1 1 200px', maxWidth: { xs: '100%', sm: '48%', md: '20%' } }}>
          <DashboardCard
            title={t('dashboard.customers')}
            value={customersCount}
            icon={<PeopleIcon />}
            onClick={() => navigate('/customers')}
            loading={orgsLoading}
          />
        </Box>
        <Box sx={{ flex: '1 1 200px', maxWidth: { xs: '100%', sm: '48%', md: '20%' } }}>
          <DashboardCard
            title={t('dashboard.activeSubscriptions')}
            value={activeSubscriptions}
            subtitle={t('dashboard.ofTotal', { count: subscriptions.length })}
            icon={<SubscriptionsIcon />}
            onClick={() => navigate('/subscriptions')}
            loading={subsLoading}
            color={chartColors.success}
          />
        </Box>
        <Box sx={{ flex: '1 1 280px', maxWidth: { xs: '100%', sm: '48%', md: '24%' } }}>
          <LicencesByAppCard
            gastroCount={licencesGastroCount}
            retailCount={licencesRetailCount}
            loading={licencesSplitLoading}
            onClick={() => navigate('/licences')}
          />
        </Box>
        <Box sx={{ flex: '1 1 200px', maxWidth: { xs: '100%', sm: '48%', md: '20%' } }}>
          <DashboardCard
            title={t('dashboard.openInvoices')}
            value={sentInvoices + overdueInvoices}
            subtitle={
              overdueInvoices > 0
                ? t('dashboard.overdueCount', { count: overdueInvoices })
                : undefined
            }
            icon={<ReceiptIcon />}
            onClick={() => navigate('/invoices')}
            loading={docsLoading}
            color={overdueInvoices > 0 ? chartColors.error : chartColors.info}
          />
        </Box>
        <Box sx={{ flex: '1 1 200px', maxWidth: { xs: '100%', sm: '48%', md: '20%' } }}>
          <DashboardCard
            title={t('dashboard.payments')}
            value={completedPayments}
            subtitle={t('dashboard.paymentsSubtitle', {
              pending: pendingPayments,
              failed: failedPayments,
            })}
            icon={<PaymentIcon />}
            onClick={() => navigate('/payments')}
            loading={paymentsLoading}
            color={failedPayments > 0 ? chartColors.error : chartColors.success}
          />
        </Box>
        <Box sx={{ flex: '1 1 200px', maxWidth: { xs: '100%', sm: '48%', md: '20%' } }}>
          <DashboardCard
            title={t('dashboard.revenuePaid')}
            value={formatCurrency(totalRevenue)}
            subtitle={
              openRevenue > 0
                ? t('dashboard.openAmount', { amount: formatCurrency(openRevenue) })
                : undefined
            }
            icon={<TrendingUpIcon />}
            loading={docsLoading}
            color={chartColors.primary}
          />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Charts Row */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ flex: '1 1 300px', minWidth: 300 }}>
            <ChartCard title={t('dashboard.subscriptionsByStatus')} loading={subsLoading}>
              {subscriptionChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={subscriptionChartData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={70}
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                      {subscriptionChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                renderChartEmpty(
                  <SubscriptionsIcon />,
                  'dashboard.noSubscriptions',
                  'dashboard.emptyState.subscriptionsHint',
                )
              )}
            </ChartCard>
          </Box>

          <Box sx={{ flex: '1 1 300px', minWidth: 300 }}>
            <ChartCard title={t('dashboard.invoicesByStatus')} loading={docsLoading}>
              {invoiceChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={invoiceChartData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={70}
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                      {invoiceChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                renderChartEmpty(
                  <ReceiptIcon />,
                  'dashboard.noInvoices',
                  'dashboard.emptyState.invoicesHint',
                )
              )}
            </ChartCard>
          </Box>

          <Box sx={{ flex: '1 1 300px', minWidth: 300 }}>
            <ChartCard title={t('dashboard.paymentsByStatus')} loading={paymentsLoading}>
              {paymentChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={paymentChartData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={70}
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                      {paymentChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                renderChartEmpty(
                  <PaymentIcon />,
                  'dashboard.noPayments',
                  'dashboard.emptyState.paymentsHint',
                )
              )}
            </ChartCard>
          </Box>
        </Box>

        {/* Revenue Chart */}
        <ChartCard title={t('dashboard.monthlyRevenue')} loading={docsLoading}>
          {monthlyRevenueData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={monthlyRevenueData}>
                <XAxis dataKey="month" />
                <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}k €`} />
                <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                <Bar dataKey="revenue" fill={chartColors.primary} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            renderChartEmpty(
              <BarChartIcon />,
              'dashboard.noRevenueData',
              'dashboard.emptyState.revenueHint',
            )
          )}
        </ChartCard>

        {!isEmptyDashboard ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ flex: '1 1 400px' }}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 2,
                    mb: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  <Typography variant="h6">{t('dashboard.latestCustomers')}</Typography>
                  <CustomerInviteButton size="small" />
                </Box>
                {customers.slice(0, 5).map((c) => (
                  <Box
                    key={c.id}
                    sx={{
                      py: 1,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                    onClick={() => navigate(`/customers/${c.id}`)}
                  >
                    <Typography variant="body2">{c.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(c.createdAt).toLocaleDateString(locale)}
                    </Typography>
                  </Box>
                ))}
              </Paper>
            </Box>

            <Box sx={{ flex: '1 1 400px' }}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Typography variant="h6" gutterBottom>
                  {t('dashboard.overview')}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">{t('dashboard.subscriptionsTotal')}</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {subscriptions.length}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">{t('dashboard.invoicesTotal')}</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {invoices.length}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">{t('dashboard.paymentsTotal')}</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {payments.length}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      pt: 1,
                      borderTop: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant="body2">{t('dashboard.totalRevenue')}</Typography>
                    <Typography variant="body2" fontWeight="bold" color="success.main">
                      {formatCurrency(totalRevenue)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">{t('dashboard.pending')}</Typography>
                    <Typography variant="body2" fontWeight="bold" color="warning.main">
                      {formatCurrency(openRevenue)}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Box>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}
